import { memo, useMemo, useCallback } from 'react';
import type { TFunction } from 'i18next';
import { PerformanceProfiler } from '../../../../components/PerformanceProfiler';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  flexRender,
  type ColumnDef,
} from '@tanstack/react-table';
import {
  Table,
  Badge,
  Text,
  Stack,
  Group,
  ActionIcon,
  Button,
  Flex,
  Box,
  Loader,
  Card,
  Select,
  Menu,
} from '@mantine/core';
import {
  Circle,
  Eye,
  MoreVertical,
  ChevronLeft,
  ChevronRight,
  Trash2,
} from 'lucide-react';
import { SessionCategoryCell } from '../SessionCategoryCell';

interface SessionsTableProps {
  data: any[];
  loading: boolean;
  totalRecords: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  navigate: (path: string) => void;
  deleteInProgress?: number;
  confirmDelete: (id: number) => void;
  isMobile: boolean;
  isTablet: boolean;
  t: TFunction;
  selectedStatuses?: ('in-progress' | 'queued' | 'completed')[];
  availableStatuses?: string[];
  onStatusChange?: (
    statuses: ('in-progress' | 'queued' | 'completed')[]
  ) => void;
  hasOtherFilters?: boolean;
}

// Componente memoizzato per Status Badge con dot
const StatusBadgeWithDot = memo<{ status: string }>(({ status }) => {
  const getStatusConfig = useMemo(() => {
    switch (status) {
      case 'in-progress':
        return {
          color: 'blue' as const,
          text: 'In corso',
          dotColor: 'var(--mantine-color-blue-6)',
        };
      case 'queued':
        return {
          color: 'orange' as const,
          text: 'Programmata',
          dotColor: 'var(--mantine-color-orange-6)',
        };
      case 'completed':
        return {
          color: 'green' as const,
          text: 'Completata',
          dotColor: 'var(--mantine-color-green-6)',
        };
      case 'connecting':
        return {
          color: 'orange' as const,
          text: 'Connessione',
          dotColor: 'var(--mantine-color-orange-6)',
        };
      case 'no-signal':
        return {
          color: 'red' as const,
          text: 'Nessun Segnale',
          dotColor: 'var(--mantine-color-red-6)',
        };
      default:
        return {
          color: 'gray' as const,
          text: 'Sconosciuto',
          dotColor: 'var(--mantine-color-gray-6)',
        };
    }
  }, [status]);

  const config = getStatusConfig;

  return (
    <Badge
      color={config.color}
      variant='light'
      size='sm'
      leftSection={
        <Circle size={6} fill={config.dotColor} color={config.dotColor} />
      }
    >
      {config.text}
    </Badge>
  );
});

StatusBadgeWithDot.displayName = 'StatusBadgeWithDot';

// Componente memoizzato per Actions Cell
const ActionsCell = memo<{
  record: any;
  navigate: (path: string) => void;
  deleteInProgress?: number;
  confirmDelete: (id: number) => void;
}>(({ record, navigate, deleteInProgress, confirmDelete }) => {
  const handleView = useCallback(() => {
    navigate('/sessions/' + record.id);
  }, [navigate, record.id]);

  const handleDelete = useCallback(() => {
    confirmDelete(record.id);
  }, [confirmDelete, record.id]);

  return (
    <Group gap='xs'>
      <Button
        variant='subtle'
        size='sm'
        leftSection={<Eye size={16} />}
        onClick={handleView}
      >
        Apri
      </Button>
      <Menu position='bottom-end' withArrow>
        <Menu.Target>
          <ActionIcon
            variant='subtle'
            size='sm'
            color='gray'
            loading={deleteInProgress === record.id}
            disabled={deleteInProgress === record.id}
          >
            <MoreVertical size={16} />
          </ActionIcon>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<Trash2 size={16} />}
            color='red'
            onClick={handleDelete}
            disabled={deleteInProgress === record.id}
          >
            Elimina sessione
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
    </Group>
  );
});

ActionsCell.displayName = 'ActionsCell';

// Componente memoizzato per Paginazione
const SessionsPagination = memo<{
  totalRecords: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}>(({ totalRecords, page, pageSize, onPageChange, onPageSizeChange }) => {
  const totalPages = useMemo(
    () => Math.ceil(totalRecords / pageSize),
    [totalRecords, pageSize]
  );

  const startRecord = useMemo(
    () => (page - 1) * pageSize + 1,
    [page, pageSize]
  );

  const endRecord = useMemo(
    () => Math.min(page * pageSize, totalRecords),
    [page, pageSize, totalRecords]
  );

  const handlePrevious = useCallback(() => {
    if (page > 1) {
      onPageChange(page - 1);
    }
  }, [page, onPageChange]);

  const handleNext = useCallback(() => {
    if (page < totalPages) {
      onPageChange(page + 1);
    }
  }, [page, totalPages, onPageChange]);

  const handlePageClick = useCallback(
    (newPage: number) => {
      onPageChange(newPage);
    },
    [onPageChange]
  );

  const pageNumbers = useMemo(() => {
    const pages: (number | string)[] = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (page <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (page >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = page - 1; i <= page + 1; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  }, [page, totalPages]);

  const handlePageSizeChange = useCallback(
    (value: string | null) => {
      if (value) {
        const newPageSize = Number(value);
        onPageSizeChange(newPageSize);
        // Reset to first page when changing page size to show the new total
        onPageChange(1);
      }
    },
    [onPageSizeChange, onPageChange]
  );

  return (
    <Flex justify='space-between' align='center' mt='md' wrap='wrap' gap='md'>
      <Group gap='sm' align='center'>
        <Text size='sm' c='dimmed'>
          Mostrate {startRecord}-{endRecord} sessioni su {totalRecords}
        </Text>
        <Select
          value={pageSize.toString()}
          onChange={handlePageSizeChange}
          data={[
            { value: '10', label: '10' },
            { value: '20', label: '20' },
            { value: '50', label: '50' },
            { value: '100', label: '100' },
          ]}
          size='sm'
          w={80}
          radius='sm'
        />
      </Group>
      <Group gap='xs'>
        <Button
          variant='outline'
          size='sm'
          radius='sm'
          onClick={handlePrevious}
          disabled={page === 1}
          leftSection={<ChevronLeft size={16} />}
        >
          Previous
        </Button>
        {pageNumbers.map((pageNum, index) => {
          if (pageNum === '...') {
            return (
              <Text key={`ellipsis-${index}`} size='sm' c='dimmed' px='xs'>
                ...
              </Text>
            );
          }
          const pageNumber = pageNum as number;
          const isSelected = pageNumber === page;
          return (
            <Button
              key={pageNumber}
              variant={isSelected ? 'filled' : 'subtle'}
              color={isSelected ? 'blue' : 'gray'}
              size='sm'
              radius='sm'
              onClick={() => handlePageClick(pageNumber)}
            >
              {pageNumber}
            </Button>
          );
        })}
        <Button
          variant='filled'
          color='blue'
          size='sm'
          radius='sm'
          onClick={handleNext}
          disabled={page >= totalPages}
          rightSection={<ChevronRight size={16} />}
        >
          Avanti
        </Button>
      </Group>
    </Flex>
  );
});

SessionsPagination.displayName = 'SessionsPagination';

// Componente principale
export const SessionsTable = memo<SessionsTableProps>(
  ({
    data,
    loading,
    totalRecords,
    page,
    pageSize,
    onPageChange,
    onPageSizeChange,
    navigate,
    deleteInProgress,
    confirmDelete,
    isMobile,
    isTablet,
    t,
    selectedStatuses = [],
    availableStatuses = [],
    onStatusChange,
    hasOtherFilters = false,
  }) => {
    const columnHelper = createColumnHelper<any>();

    // Helper per formattare date
    const formatDate = useCallback((date: Date) => {
      return date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    }, []);

    const formatTime = useCallback((date: Date) => {
      return date.toLocaleTimeString('it-IT', {
        hour: '2-digit',
        minute: '2-digit',
      });
    }, []);

    // Definizione colonne
    const columns = useMemo<ColumnDef<any>[]>(() => {
      const baseColumns: ColumnDef<any>[] = [
        columnHelper.display({
          id: 'status',
          header: 'Stato',
          minSize: 120,
          size: 120,
          cell: ({ row }) => (
            <StatusBadgeWithDot status={row.original.status} />
          ),
        }),
        columnHelper.display({
          id: 'type',
          header: 'Categorie di Esperienze',
          cell: ({ row }) => (
            <SessionCategoryCell
              type={row.original.type}
              scenario={row.original.scenario}
              source={row.original.experienceSource}
            />
          ),
        }),
        columnHelper.display({
          id: 'patientCode',
          header: 'Utente',
          cell: ({ row }) => {
            const record = row.original;
            const userDisplay = {
              main: record.patientCode || 'N/A',
              subtitle: record.patientCode ? `ID HR-${record.id}` : undefined,
            };
            return (
              <Stack gap={2}>
                <Text size='sm' fw={500}>
                  {userDisplay.main}
                </Text>
                {userDisplay.subtitle && (
                  <Text size='xs' c='dimmed'>
                    {userDisplay.subtitle}
                  </Text>
                )}
              </Stack>
            );
          },
        }),
        columnHelper.display({
          id: 'device',
          header: 'Dispositivo',
          cell: ({ row }) => {
            const record = row.original;
            const deviceDisplay = isMobile
              ? {
                  main: record.deviceName || 'N/A',
                  subtitle: undefined,
                }
              : {
                  main: record.deviceName || 'N/A',
                  subtitle: `ID: ${record.clientId || 'N/A'} · Model: ${record.deviceModel || 'N/A'}`,
                };
            return (
              <Stack gap={2}>
                <Text size='sm' fw={500}>
                  {deviceDisplay.main}
                </Text>
                {deviceDisplay.subtitle && (
                  <Text size='xs' c='dimmed'>
                    {deviceDisplay.subtitle}
                  </Text>
                )}
              </Stack>
            );
          },
        }),
        columnHelper.display({
          id: 'date',
          header: 'Data creazione',
          minSize: 180,
          size: 180,
          cell: ({ row }) => {
            const record = row.original;
            const updatedDate = record.updated_at
              ? new Date(record.updated_at)
              : null;
            const createdDate = record.created_at
              ? new Date(record.created_at)
              : null;

            let dateInfo;
            if (updatedDate) {
              dateInfo = {
                main: `${formatDate(updatedDate)} · ${formatTime(updatedDate)}`,
                subtitle: createdDate ? 'Creato' : undefined,
              };
            } else if (createdDate) {
              dateInfo = {
                main: `${formatDate(createdDate)} · ${formatTime(createdDate)}`,
                subtitle: undefined,
              };
            } else {
              dateInfo = { main: 'N/A', subtitle: undefined };
            }

            return (
              <Stack gap={2}>
                <Text size='sm' fw={500}>
                  {dateInfo.main}
                </Text>
                {dateInfo.subtitle && (
                  <Text size='xs' c='dimmed'>
                    {dateInfo.subtitle}
                  </Text>
                )}
              </Stack>
            );
          },
        }),
        columnHelper.display({
          id: 'actions',
          header: 'Azioni',
          minSize: 100,
          size: 100,
          cell: ({ row }) => (
            <ActionsCell
              record={row.original}
              navigate={navigate}
              deleteInProgress={deleteInProgress}
              confirmDelete={confirmDelete}
            />
          ),
        }),
      ];

      // Filtrare colonne in base a responsive
      if (isMobile) {
        return baseColumns.filter(
          col =>
            col.id === 'status' || col.id === 'device' || col.id === 'actions'
        );
      }

      if (isTablet) {
        return baseColumns.filter(col => col.id !== 'patientCode');
      }

      return baseColumns;
    }, [
      columnHelper,
      isMobile,
      isTablet,
      t,
      formatDate,
      formatTime,
      navigate,
      deleteInProgress,
      confirmDelete,
    ]);

    const table = useReactTable({
      data,
      columns,
      getCoreRowModel: getCoreRowModel(),
    });

    if (loading) {
      return (
        <PerformanceProfiler id='SessionsTable'>
          <Box ta='center' py='xl'>
            <Loader size='md' />
          </Box>
        </PerformanceProfiler>
      );
    }

    if (data.length === 0) {
      const showSuggestion =
        selectedStatuses.length === 1 &&
        !hasOtherFilters &&
        onStatusChange &&
        availableStatuses.length > 0;

      const getStatusLabel = (status: string) => {
        switch (status) {
          case 'in-progress':
            return 'In corso';
          case 'queued':
            return 'Programmate';
          case 'completed':
            return 'Completate';
          default:
            return status;
        }
      };

      const getSuggestion = () => {
        if (!showSuggestion) return null;
        const currentStatus = selectedStatuses[0];
        if (
          currentStatus === 'in-progress' &&
          availableStatuses.includes('queued')
        ) {
          return {
            suggestionText: 'Prova a visualizzare le Sessioni Programmate.',
            buttonLabel: 'Mostra Sessioni Programmate',
            action: () => onStatusChange(['queued']),
          };
        }
        if (
          currentStatus === 'in-progress' &&
          !availableStatuses.includes('queued') &&
          availableStatuses.includes('completed')
        ) {
          return {
            suggestionText: 'Prova a visualizzare le sessioni completate.',
            buttonLabel: 'Mostra completate',
            action: () => onStatusChange(['completed']),
          };
        }
        if (
          currentStatus === 'queued' &&
          availableStatuses.includes('in-progress')
        ) {
          return {
            suggestionText: 'Prova a visualizzare le Sessioni in corso.',
            buttonLabel: 'Mostra Sessioni in corso',
            action: () => onStatusChange(['in-progress']),
          };
        }
        if (
          currentStatus === 'queued' &&
          !availableStatuses.includes('in-progress') &&
          availableStatuses.includes('completed')
        ) {
          return {
            suggestionText: 'Prova a visualizzare le sessioni completate.',
            buttonLabel: 'Mostra completate',
            action: () => onStatusChange(['completed']),
          };
        }
        if (
          currentStatus === 'completed' &&
          availableStatuses.includes('in-progress')
        ) {
          return {
            suggestionText: 'Prova a visualizzare le sessioni in corso.',
            buttonLabel: 'Mostra Sessioni in corso',
            action: () => onStatusChange(['in-progress']),
          };
        }
        if (
          currentStatus === 'completed' &&
          !availableStatuses.includes('in-progress') &&
          availableStatuses.includes('queued')
        ) {
          return {
            suggestionText: 'Prova a visualizzare le Sessioni Programmate.',
            buttonLabel: 'Mostra Sessioni Programmate',
            action: () => onStatusChange(['queued']),
          };
        }
        return null;
      };

      const suggestion = getSuggestion();
      const currentStatusLabel =
        selectedStatuses.length === 1
          ? getStatusLabel(selectedStatuses[0])
          : null;

      return (
        <PerformanceProfiler id='SessionsTable'>
          <Card shadow='sm' radius='sm' withBorder p='md'>
            <Stack gap='md'>
              <Box>
                <Text size='sm' fw={600} mb={4}>
                  Sessioni
                </Text>
                <Text size='xs' c='dimmed'>
                  Visualizza e gestisci tutte le sessioni disponibili
                </Text>
              </Box>
              <Box ta='center' py='xl'>
                <Stack gap='md' align='center'>
                  <Text size='sm' c='dimmed'>
                    {currentStatusLabel
                      ? `Nessuna sessione "${currentStatusLabel}" trovata`
                      : t('pa_no_sessions', 'Nessuna sessione trovata')}
                  </Text>
                  {suggestion && (
                    <>
                      <Text size='xs' c='dimmed'>
                        {suggestion.suggestionText}
                      </Text>
                      <Button
                        variant='light'
                        size='sm'
                        onClick={suggestion.action}
                      >
                        {suggestion.buttonLabel}
                      </Button>
                    </>
                  )}
                </Stack>
              </Box>
            </Stack>
          </Card>
        </PerformanceProfiler>
      );
    }

    return (
      <PerformanceProfiler id='SessionsTable'>
        <Card shadow='sm' radius='sm' withBorder p='md'>
          <Stack gap='md'>
            <Box>
              <Text size='sm' fw={600} mb={4}>
                Sessioni
              </Text>
              <Text size='xs' c='dimmed'>
                Visualizza e gestisci tutte le sessioni disponibili
              </Text>
            </Box>
            <Table
              verticalSpacing='xs'
              horizontalSpacing='sm'
              highlightOnHover
              withTableBorder
            >
              <Table.Thead>
                {table.getHeaderGroups().map(headerGroup => (
                  <Table.Tr
                    key={headerGroup.id}
                    bg='var(--mantine-color-gray-0)'
                  >
                    {headerGroup.headers.map(header => (
                      <Table.Th key={header.id} p='sm' fw={500}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </Table.Th>
                    ))}
                  </Table.Tr>
                ))}
              </Table.Thead>
              <Table.Tbody>
                {table.getRowModel().rows.map(row => (
                  <Table.Tr key={row.id}>
                    {row.getVisibleCells().map(cell => (
                      <Table.Td key={cell.id} p='sm'>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </Table.Td>
                    ))}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
            <SessionsPagination
              totalRecords={totalRecords}
              page={page}
              pageSize={pageSize}
              onPageChange={onPageChange}
              onPageSizeChange={onPageSizeChange}
            />
          </Stack>
        </Card>
      </PerformanceProfiler>
    );
  }
);

SessionsTable.displayName = 'SessionsTable';
